import React, {createContext, useState, useContext, ReactNode, useCallback, useMemo} from 'react';

import {UserCreditCardsDTO} from '../dtos/UserCreditCardsDTO';
import {api} from '../services/api';

interface FetchCards {
  onFinish?: () => void;
  cardNumber?: string;
}

interface CardsContextData {
  cardBrandImage: (brand: string) => string;
  fetchUserCreditCardList: ({onFinish, cardNumber}: FetchCards) => void;
  userCards: UserCreditCardsDTO[];
  handleClearUserCardsList: () => void;
  isLoadingCards: boolean;
}

interface CardsProviderProps {
  children: ReactNode;
}

const CardsContext = createContext<CardsContextData>(
  {} as CardsContextData,
);

function CardsProvider({children}: CardsProviderProps) {
  const [userCards, setUserCards] = useState<UserCreditCardsDTO[]>(
    [] as UserCreditCardsDTO[],
  );
  const [isLoadingCards, setIsLoadingCards] = useState(false);

  const handleClearUserCardsList = useCallback(() => {
    setUserCards([]);
  }, []);

  const cardBrandImage = useCallback((brand: string) => {
    let brandImage = '';

    switch (brand) {
      case 'Master':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/mastercard.png';
        break;
      case 'Visa':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/visa.png';
        break;
      case 'Amex':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/amex.png';
        break;
      case 'Elo':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/elo.png';
        break;
      case 'Hiper':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/hipercard.png';
        break;
      case 'vr':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/vr.webp';
        break;
      case 'pluxee':
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/pluxee.webp';
        break;
      default:
        brandImage =
          'https://d3ag5cip9ldvh3.cloudfront.net/card_brands/creditcard.png';
    }

    return brandImage;
  }, []);

  const fetchUserCreditCardList = useCallback(async ({onFinish}: FetchCards) => {
    setIsLoadingCards(true);
    try {
      await api
        .get('/cred_card')
        .then(async res => {
          console.log('🚀 ~ fetchUserCreditCardList ~ res:', res);
          let cards = [] as UserCreditCardsDTO[];

          {
            const activeCards = res.data.sort(function (
              x: UserCreditCardsDTO,
              y: UserCreditCardsDTO,
            ) {
              let a = y.confirmaValidaCard ? 1 : 0;
              let b = x.confirmaValidaCard ? 1 : 0;
              return a - b;
            });

            activeCards.forEach((item: any) => {
              let brand = cardBrandImage(item.brand);

              let card: UserCreditCardsDTO = {
                id: item._id.$oid,
                brand: item.brand,
                confirmaValidaCard: item.confirmaValidaCard,
                dataConfirmaValidaCard: item.dataConfirmaValidaCard,
                expiration: item.expiration,
                name: item.nome,
                voucher: item.voucher,
                icon: brand,
                number: item.number,
                security_code: item.security_code,
                tentativa_validacard: item.tentativa_validacard,
                created_at: item.created_at,
                updated_at: item.updated_at,
                cep: item?.cep,
                cidade: item?.cidade,
                complemento: item?.complemento,
                endereco: item?.endereco,
                estado: item?.estado,
                numero: item?.numero,
                brb: item?.brb,
                primeiraCompra: item?.primeiraCompra,
              };
              cards.push(card);
            });
          }

          setUserCards(cards);
        })
        .catch(({response}) => {
          console.log(response.data);
        });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingCards(false);
      if (onFinish) {
        onFinish();
      }
    }
  }, [cardBrandImage]);

  const value = useMemo(
    () => ({
      handleClearUserCardsList,
      cardBrandImage,
      userCards,
      fetchUserCreditCardList,
      isLoadingCards,
    }),
    [
      handleClearUserCardsList,
      cardBrandImage,
      userCards,
      fetchUserCreditCardList,
      isLoadingCards,
    ],
  );

  return (
    <CardsContext.Provider value={value}>
      {children}
    </CardsContext.Provider>
  );
}

function useCards() {
  const context = useContext(CardsContext);
  return context;
}

export {useCards, CardsProvider};